// Runway tab — list of runways with quick-stats; tap a row to drill in.
//
// Source-of-truth path is FAA NASR APT.txt + ILS.txt joined: the
// /api/airport-nasr endpoint emits a per-end record with end-elev,
// displaced thresholds, traffic-pattern direction, ILS frequency +
// magnetic course, REIL / PAPI / VASI / centerline / touchdown
// lights, LAHSO availability, end coordinates, and TORA/TODA/ASDA/LDA.
//
// Falls back to the OurAirports `runways.csv` data (carried on the
// AirportDetail prop) when NASR doesn't have the airport, e.g. for
// non-US airports.

import { useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import type { AirportDetail, Runway } from '../../lib/airports';
import { formatLatLonDMM } from '../../lib/coords';
import {
  fetchNasrAirport,
  type NasrRunway,
  type NasrRunwayEnd,
  type NasrAirportDetail,
} from '../../lib/airportNasr';
import RunwayDiagram from '../RunwayDiagram';

export function RunwayTab({ detail }: { detail: AirportDetail }) {
  // NASR mirror — null while loading or when the ident isn't in the
  // FAA dataset (e.g. non-US airports).
  const ident = detail.airport.ident;
  const { data: nasrResult, loading: nasrLoading } = useFetch(
    () => fetchNasrAirport(ident).catch(() => null),
    [ident],
  );
  const nasr = nasrResult?.airport ?? null;

  // Selection state. We use the OurAirports Runway as the canonical
  // shape for "which row is selected", since some airports may have
  // OurAirports rows that NASR is missing (and we still want the user
  // to be able to drill into them with degraded fields). When NASR
  // has the same designator pair we paint the rich detail; otherwise
  // we fall back to the simple stub.
  const [selected, setSelected] = useState<Runway | null>(null);

  const runways = detail.runways || [];

  if (selected) {
    const matchedNasr = nasr ? findNasrMatch(nasr.runways, selected) : null;
    return (
      <RunwayDetail
        runway={selected}
        nasr={matchedNasr}
        airportMagVar={nasr?.magVar ?? null}
        onBack={() => setSelected(null)}
      />
    );
  }

  if (runways.length === 0 && (!nasr || nasr.runways.length === 0)) {
    return <div className="p-4 text-sm text-cockpit-400 italic">No runways on file.</div>;
  }

  return (
    <div>
      <RunwayDiagram nasr={nasr} />
      <ul className="divide-y divide-cockpit-700">
        {runways.map((r, i) => (
        <li key={`${r.le_ident}-${r.he_ident}-${i}`}>
          <button
            onClick={() => setSelected(r)}
            className="w-full text-left px-4 py-3 hover:bg-cockpit-700 active:bg-cockpit-600 transition-colors"
            disabled={r.closed}
            aria-label={`Runway ${r.le_ident}/${r.he_ident}`}
          >
            <div className="flex items-baseline justify-between gap-2">
              <div className="font-mono font-semibold text-cockpit-100 text-base">
                {r.le_ident}/{r.he_ident}
              </div>
              {r.closed && (
                <span className="text-[10px] tracking-widest bg-red-900/50 text-red-300 px-1.5 py-0.5 rounded">
                  CLOSED
                </span>
              )}
            </div>
            <div className="text-xs text-cockpit-300 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
              <span className="font-mono">
                {r.length_ft != null ? `${r.length_ft.toLocaleString()}'` : '—'}
                {r.width_ft != null ? ` × ${r.width_ft}'` : ''}
              </span>
              <span>{describeSurface(r.surface)}</span>
              {r.lighted && <span className="text-amber-400/80">Lighted</span>}
            </div>
          </button>
        </li>
      ))}
        {nasrLoading && (
          <li className="px-4 py-2 text-[10px] text-cockpit-400 italic">Loading FAA NASR…</li>
        )}
      </ul>
    </div>
  );
}

/** Match a NASR runway to an OurAirports Runway by designator pair. */
function findNasrMatch(nasrRunways: NasrRunway[], r: Runway): NasrRunway | null {
  const key = `${r.le_ident}/${r.he_ident}`.toUpperCase();
  for (const n of nasrRunways) {
    if (n.ident.toUpperCase() === key) return n;
    // FAA sometimes orders the designator pair differently from OA
    // (e.g. "28R/10L" instead of "10L/28R"); accept either order.
    const reversed = `${r.he_ident}/${r.le_ident}`.toUpperCase();
    if (n.ident.toUpperCase() === reversed) return n;
  }
  return null;
}

// ---- detail view ----

function RunwayDetail({
  runway, nasr, airportMagVar, onBack,
}: {
  runway: Runway;
  nasr: NasrRunway | null;
  airportMagVar: number | null;
  onBack: () => void;
}) {
  const r = runway;

  // Pair the OurAirports designators to the NASR ends. NASR's baseEnd
  // is whichever end is listed first in the combined ident; that
  // usually matches OA's `le_ident` but sometimes the pair is
  // reversed, in which case we swap.
  let nasrLow: NasrRunwayEnd | null = nasr?.baseEnd ?? null;
  let nasrHigh: NasrRunwayEnd | null = nasr?.recipEnd ?? null;
  if (nasr && nasrLow && nasrLow.ident.toUpperCase() !== r.le_ident.toUpperCase()) {
    [nasrLow, nasrHigh] = [nasrHigh, nasrLow];
  }

  // Length / width / surface row prefers NASR (truthy from FAA) when
  // available and adds a small "FAA" badge so the user knows which
  // source they're seeing.
  const lengthFt = nasr?.lengthFt ?? r.length_ft ?? null;
  const widthFt = nasr?.widthFt ?? r.width_ft ?? null;
  const surface = nasr?.surface ?? r.surface;

  return (
    <div className="px-4 py-3 space-y-4 text-sm">
      <button
        onClick={onBack}
        className="text-xs text-cockpit-300 hover:text-cockpit-100 flex items-center gap-1"
        aria-label="Back to runway list"
      >
        ← All runways
      </button>

      <div>
        <div className="font-mono text-2xl text-cockpit-100">{r.le_ident}/{r.he_ident}</div>
        <div className="text-xs text-cockpit-300 mt-0.5">
          {lengthFt != null ? `${lengthFt.toLocaleString()}'` : '—'}
          {widthFt != null ? ` × ${widthFt}'` : ''}
          {' · '}
          {describeSurface(surface)} · {r.lighted ? 'lighted' : 'unlit'}
          {r.closed && ' · CLOSED'}
          {nasr && (
            <span className="ml-2 text-[10px] tracking-widest text-cockpit-400">FAA NASR</span>
          )}
        </div>
      </div>

      <Section title="Low end">
        <RunwayEnd
          oaIdent={r.le_ident}
          oaHeading={r.le_heading}
          nasr={nasrLow}
          airportMagVar={airportMagVar}
        />
      </Section>
      <Section title="High end">
        <RunwayEnd
          oaIdent={r.he_ident}
          oaHeading={r.he_heading}
          nasr={nasrHigh}
          airportMagVar={airportMagVar}
        />
      </Section>

      {nasr && (nasrLow?.lahsoLdaFt != null || nasrHigh?.lahsoLdaFt != null) && (
        <Section title="LAHSO">
          {nasrLow?.lahsoLdaFt != null && (
            <LahsoBlock end={nasrLow} />
          )}
          {nasrHigh?.lahsoLdaFt != null && (
            <LahsoBlock end={nasrHigh} />
          )}
        </Section>
      )}

      {!nasr && (
        <div className="bg-cockpit-700/20 border border-dashed border-cockpit-600 rounded-md px-3 py-2 text-[11px] text-cockpit-400">
          End-elevation, displaced thresholds, ILS frequency, REIL/PAPI/VASI,
          and LAHSO availability come from the FAA NASR feed. This airport
          isn't in the current NASR cycle (likely non-US); the basics above
          come from the OurAirports CSV instead.
        </div>
      )}
    </div>
  );
}

function RunwayEnd({
  oaIdent, oaHeading, nasr, airportMagVar,
}: {
  oaIdent: string;
  oaHeading: number | null;
  nasr: NasrRunwayEnd | null;
  airportMagVar: number | null;
}) {
  // Bearing: prefer ILS magnetic course when published (most precise),
  // else NASR true alignment converted to magnetic via the airport's
  // magvar, else fall back to OurAirports' published heading.
  const trueDeg = nasr?.trueAlignmentDeg ?? oaHeading ?? null;
  const magDeg =
    nasr?.ils?.magneticCourseDeg
    ?? (trueDeg != null && airportMagVar != null
        ? ((trueDeg - airportMagVar) % 360 + 360) % 360
        : null);
  const ils = nasr?.ils ?? null;
  const lighting = nasr ? lightingDescription(nasr) : '';
  return (
    <div className="bg-cockpit-700/30 border border-cockpit-700 rounded-md px-3 py-2 space-y-1">
      <Kv k="Designator" v={nasr?.ident || oaIdent} mono />
      {trueDeg != null && (
        <Kv k="True heading" v={`${formatHeading(trueDeg)}°`} />
      )}
      {magDeg != null && (
        <Kv k="Magnetic heading" v={`${formatHeading(magDeg)}°`} />
      )}
      {nasr && (
        <>
          <Kv
            k="Traffic pattern"
            v={nasr.rightTrafficPattern ? 'Right-hand' : 'Left-hand'}
          />
          {nasr.endElevFt != null && (
            <Kv k="End elevation" v={`${Math.round(nasr.endElevFt).toLocaleString()} ft MSL`} />
          )}
          {nasr.touchdownZoneElevFt != null && (
            <Kv k="Touchdown zone" v={`${Math.round(nasr.touchdownZoneElevFt).toLocaleString()} ft MSL`} />
          )}
          {nasr.displacedThresholdLengthFt != null && nasr.displacedThresholdLengthFt > 0 && (
            <>
              <Kv k="Displaced threshold" v={`${nasr.displacedThresholdLengthFt.toLocaleString()} ft`} />
              {nasr.displacedThresholdElevFt != null && (
                <Kv k="Threshold elev" v={`${Math.round(nasr.displacedThresholdElevFt).toLocaleString()} ft MSL`} />
              )}
            </>
          )}
          {nasr.endLat != null && nasr.endLon != null && (
            <Kv k="End coords" v={formatLatLonDMM(nasr.endLat, nasr.endLon)} mono />
          )}
          {lighting && <Kv k="Lighting" v={lighting} />}
          {nasr.glidePathDeg != null && nasr.glidePathDeg > 0 && (
            <Kv k="Glide path" v={`${nasr.glidePathDeg.toFixed(2)}°`} />
          )}
          {nasr.thresholdCrossingHeightFt != null && nasr.thresholdCrossingHeightFt > 0 && (
            <Kv k="Threshold crossing" v={`${nasr.thresholdCrossingHeightFt} ft AGL`} />
          )}
          {nasr.toraFt != null && nasr.toraFt > 0 && (
            <Kv k="TORA" v={`${nasr.toraFt.toLocaleString()} ft`} />
          )}
          {nasr.ldaFt != null && nasr.ldaFt > 0 && (
            <Kv k="LDA"  v={`${nasr.ldaFt.toLocaleString()} ft`} />
          )}
        </>
      )}
      {ils && (
        <div className="mt-2 pt-2 border-t border-cockpit-700 space-y-1">
          <div className="text-[10px] tracking-widest text-cockpit-400">{ils.type || 'ILS'}</div>
          {ils.localizerMhz != null && (
            <Kv k="Localizer freq" v={`${ils.localizerMhz.toFixed(2)} MHz`} mono />
          )}
          {ils.magneticCourseDeg != null && (
            <Kv k="Approach course" v={`${formatHeading(ils.magneticCourseDeg)}° M`} />
          )}
          {ils.category && (
            <Kv k="Category" v={ils.category} />
          )}
        </div>
      )}
    </div>
  );
}

function LahsoBlock({ end }: { end: NasrRunwayEnd }) {
  return (
    <div className="bg-cockpit-700/20 border border-cockpit-700 rounded-md px-3 py-2 space-y-0.5">
      <div className="text-[10px] tracking-widest text-cockpit-400">Landing on {end.ident}</div>
      {end.lahsoLdaFt != null && (
        <Kv k="Hold-short LDA" v={`${end.lahsoLdaFt.toLocaleString()} ft`} />
      )}
      {end.lahsoIntersection && (
        <Kv k="Hold short of" v={end.lahsoIntersection} />
      )}
    </div>
  );
}

function lightingDescription(end: NasrRunwayEnd): string {
  const parts: string[] = [];
  if (end.reil) parts.push('REIL');
  if (end.centerlineLights) parts.push('CL');
  if (end.touchdownLights) parts.push('TDZ');
  if (end.vgsi) parts.push(formatVgsi(end.vgsi));
  if (end.approachLights) parts.push(end.approachLights);
  return parts.join(' · ');
}

// FAA VGSI codes are like 'P4L' (4-box PAPI left), 'V2R' (2-bar VASI right).
function formatVgsi(code: string): string {
  const c = code.trim().toUpperCase();
  if (!c) return '';
  const m = /^([PV])(\d+)([LR]?)$/.exec(c);
  if (!m) return c;
  const family = m[1] === 'P' ? 'PAPI' : 'VASI';
  const boxes = m[2];
  const side = m[3] === 'L' ? 'L' : m[3] === 'R' ? 'R' : '';
  return `${family}-${boxes}${side ? ` ${side}` : ''}`;
}

function formatHeading(deg: number): string {
  return Math.round(deg).toString().padStart(3, '0');
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[10px] tracking-widest text-cockpit-400 mb-1">{title.toUpperCase()}</h3>
      {children}
    </section>
  );
}

function Kv({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between text-xs gap-3">
      <span className="text-cockpit-300 shrink-0">{k}</span>
      <span className={`text-cockpit-100 text-right break-words ${mono ? 'font-mono' : ''}`}>{v}</span>
    </div>
  );
}

function describeSurface(surface?: string): string {
  if (!surface) return 'unknown';
  // Both NASR (e.g. "ASPH-G") and OurAirports (e.g. "ASP") surface
  // codes are humanized here. The first token before the dash is the
  // family.
  const map: Record<string, string> = {
    ASP: 'asphalt', ASPH: 'asphalt',
    CON: 'concrete', CONC: 'concrete',
    GRS: 'grass',
    TURF: 'turf',
    TRTD: 'treated',
    GRVL: 'gravel',
    DIRT: 'dirt',
    WATER: 'water',
    SNOW: 'snow',
    ICE: 'ice',
    PEM: 'paved',
    BIT: 'bitumen',
  };
  const fam = surface.split('-')[0].trim().toUpperCase();
  return map[fam] ?? surface.toLowerCase();
}
